import { z } from "zod";

/**
 * Which providers exist, and what each is for.
 *
 * Named by vendor rather than by role because one vendor can fill several
 * roles — OpenAI transcribes and writes, OpenRouter can write and sometimes
 * see — and a list keyed by role would have to repeat them.
 */
export const aiProviderIds = ["gemini", "anthropic", "openai", "openrouter", "elevenlabs"] as const;
export type AiProviderId = (typeof aiProviderIds)[number];

/**
 * The jobs the pipeline needs done, independent of who does them.
 *
 * The pipeline asks the registry for a capability and gets whatever is
 * configured. It never learns which vendor answered, which is the only way
 * adding a provider stays a matter of writing an adapter.
 */
export const aiCapabilities = ["videoAnalysis", "script", "transcription", "voice"] as const;
export type AiCapability = (typeof aiCapabilities)[number];

/**
 * What a given model can be fed.
 *
 * Checked before a request rather than after it fails, because the failure is
 * not always loud: a text-only model handed a description of frames will
 * cheerfully answer, and the answer will be confident and uninformed.
 */
export interface AIProviderCapabilities {
  text: boolean;
  image: boolean;
  video: boolean;
  audio: boolean;
}

/**
 * How credentials are chosen.
 *
 * `individual` uses a key per vendor. `openrouter` routes the language work
 * through one account — but only the language work: transcription and speech
 * are not text completions and are left with their own providers, because
 * pretending one key covers everything is how a pipeline half-breaks.
 */
export const providerModes = ["individual", "openrouter"] as const;
export type ProviderMode = (typeof providerModes)[number];

/**
 * A scene as the application understands it, whoever described it.
 *
 * Gemini, OpenRouter and any future model return different documents; this is
 * what they are all converted into. Nothing downstream may depend on a
 * vendor's own shape — a provider swap would otherwise be a change to the
 * pipeline rather than to one adapter.
 */
export const analysedSceneSchema = z.object({
  start: z.number().nonnegative(),
  end: z.number().nonnegative(),
  description: z.string().max(600).default(""),
  /** What the user did here, in plain words. Not coordinates; not a zoom target. */
  actions: z.array(z.string().max(200)).max(40).default([]),
  visualQuality: z.enum(["good", "fair", "poor"]).default("good"),
  /** 0..1, how much this stretch matters to the finished video. */
  relevance: z.number().min(0).max(1).default(0.5),
});

export const videoAnalysisSchema = z.object({
  duration: z.number().nonnegative(),
  scenes: z.array(analysedSceneSchema).max(500).default([]),
  /** Which model produced this, for the ledger and the job log. */
  model: z.string().max(120).optional(),
  /**
   * What the call actually spent, when the provider says.
   *
   * Optional because it is unknown, not zero — a cached analysis written
   * before this was recorded has no usage, and claiming it was free is the
   * error this exists to fix. Frames were counted here instead for a while,
   * and frames cannot be priced.
   */
  usage: z.object({ inputTokens: z.number().nonnegative(), outputTokens: z.number().nonnegative() }).optional(),
});

export type AnalysedScene = z.infer<typeof analysedSceneSchema>;
export type VideoAnalysisResult = z.infer<typeof videoAnalysisSchema>;

/**
 * What a visual analyser is given.
 *
 * Frames and a transcript, never the whole file. Sending a full screen
 * recording to a multimodal model on every upload is the single most
 * expensive thing this pipeline could do, and a handful of representative
 * frames answers the same question — what is on screen, and when.
 */
export interface VideoAnalysisInput {
  durationSeconds: number;
  keyframes: { timestamp: number; imagePath: string }[];
  transcript?: { start: number; end: number; text: string }[];
  signal?: AbortSignal;
}

export interface VideoAnalysisProvider {
  readonly name: string;
  readonly capabilities: AIProviderCapabilities;
  analyzeVisuals(input: VideoAnalysisInput): Promise<VideoAnalysisResult>;
}

/** The outcome of a `Test Connection`. Never carries the key or the raw payload. */
export interface ConnectionCheck {
  provider: AiProviderId;
  ok: boolean;
  /** One sentence a user can act on. Never a stack trace or a request body. */
  detail: string;
}

/**
 * The narration audio formats worth offering.
 *
 * ElevenLabs supports more — raw PCM, μ-law — but those come back as
 * headerless samples the mixer and the media probe would each need told the
 * shape of, and a setting that silently breaks rendering is worse than one
 * that is absent. MP3 is what the renderer already handles end to end.
 *
 * 128 kbps is the default because it is transparent for speech and available
 * on every tier. The higher rate needs a paid plan; the lower ones exist for
 * anyone who cares more about file size than about the top of a sibilant.
 */
export const elevenLabsFormats = [
  "mp3_44100_128",
  "mp3_44100_192",
  "mp3_44100_96",
  "mp3_44100_64",
  "mp3_22050_32",
] as const;
export type ElevenLabsFormat = (typeof elevenLabsFormats)[number];

export const defaultElevenLabsFormat: ElevenLabsFormat = "mp3_44100_128";

/** How each reads in a list. */
export const elevenLabsFormatLabels: Record<ElevenLabsFormat, string> = {
  mp3_44100_128: "MP3 128 kbps — recommended",
  mp3_44100_192: "MP3 192 kbps — paid plans only",
  mp3_44100_96: "MP3 96 kbps",
  mp3_44100_64: "MP3 64 kbps",
  mp3_22050_32: "MP3 32 kbps — smallest",
};

/** The speech models worth offering, with the trade each makes. */
export const elevenLabsModels = ["eleven_multilingual_v2", "eleven_turbo_v2_5", "eleven_flash_v2_5"] as const;
export type ElevenLabsModel = (typeof elevenLabsModels)[number];

export const elevenLabsModelLabels: Record<ElevenLabsModel, string> = {
  eleven_multilingual_v2: "Multilingual v2 — best quality, recommended",
  eleven_turbo_v2_5: "Turbo v2.5 — faster, slightly lower quality",
  eleven_flash_v2_5: "Flash v2.5 — fastest and cheapest",
};
